import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { ArrowLeft, Zap, ShoppingBag, Check, Sparkles } from 'lucide-react';

interface CursorSkin {
  id: string;
  name: string;
  icon: string;
  price: number;
  description: string;
}

const SKINS: CursorSkin[] = [
  { id: 'default', name: '默认光标', icon: '⚪', price: 0, description: '基础圆形光标' },
  { id: 'star', name: '星星光标', icon: '⭐', price: 20, description: '闪耀的星形光标' },
  { id: 'leaf', name: '绿叶光标', icon: '🍃', price: 25, description: '森林主题光标' },
  { id: 'diamond', name: '钻石光标', icon: '💎', price: 40, description: '珍贵的菱形光标' },
  { id: 'butterfly', name: '蝴蝶光标', icon: '🦋', price: 35, description: '优雅的蝶翼光标' },
  { id: 'moon', name: '月亮光标', icon: '🌙', price: 30, description: '神秘夜空光标' },
  { id: 'flame', name: '火焰光标', icon: '🔥', price: 50, description: '炫酷火焰轨迹' },
  { id: 'rainbow', name: '彩虹光标', icon: '🌈', price: 60, description: '七彩流光光标' },
];

export default function ShopPage() {
  const navigate = useNavigate();
  const { energy, cursorSkins, activeCursor, spendEnergy, buyCursorSkin, setActiveCursor } = useGameStore();

  const handleBuy = (skin: CursorSkin) => {
    if (cursorSkins.includes(skin.id)) {
      setActiveCursor(skin.id);
      return;
    }
    if (spendEnergy(skin.price)) {
      buyCursorSkin(skin.id);
      setActiveCursor(skin.id);
    }
  };

  const containerAnim = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
  const cardAnim = { hidden: { y: 20, opacity: 0 }, show: { y: 0, opacity: 1, transition: { duration: 0.35 } } };

  return (
    <div className="min-h-screen w-full bg-[var(--bg)]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between max-w-2xl mx-auto px-4 py-3">
          <button onClick={() => navigate('/')} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-emerald-500" /> 光标商店
          </h1>
          <div className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/30 border border-amber-100 dark:border-amber-800 px-3 py-1.5 rounded-full">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{energy}</span>
          </div>
        </div>
      </header>

      <motion.div
        variants={containerAnim}
        initial="hidden"
        animate="show"
        className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-5"
      >
        {/* Current cursor preview */}
        <motion.div variants={cardAnim} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 text-center">
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">当前使用</p>
          <motion.div
            className="text-5xl mb-3 inline-block"
            animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            {SKINS.find(s => s.id === activeCursor)?.icon || '⚪'}
          </motion.div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {SKINS.find(s => s.id === activeCursor)?.name || '默认光标'}
          </p>
        </motion.div>

        {/* Skins Grid */}
        <div className="grid grid-cols-2 gap-3">
          {SKINS.map((skin, idx) => {
            const owned = cursorSkins.includes(skin.id);
            const active = activeCursor === skin.id;
            const canAfford = energy >= skin.price;

            return (
              <motion.button
                key={skin.id}
                onClick={() => handleBuy(skin)}
                variants={cardAnim}
                className={`bg-white dark:bg-gray-800 rounded-2xl p-4 text-center shadow-sm border transition-all ${
                  active ? 'border-emerald-300 dark:border-emerald-600 ring-2 ring-emerald-100 dark:ring-emerald-900/50' :
                  owned ? 'border-amber-200 dark:border-amber-700' : 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600'
                }`}
              >
                <div className="text-3xl mb-2">{skin.icon}</div>
                <p className="text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5">{skin.name}</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-2.5 line-clamp-1">{skin.description}</p>
                
                {active ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                    <Check className="w-3 h-3" /> 使用中
                  </span>
                ) : owned ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2.5 py-1 rounded-full">
                    <Sparkles className="w-3 h-3" /> 点击使用
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded-full ${
                    canAfford ? 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30' : 'text-gray-400 bg-gray-50 dark:bg-gray-700'
                  }`}>
                    <Zap className="w-3 h-3" /> {skin.price}
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Friends & Leaderboard */}
        <motion.div variants={cardAnim} className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            🏆 全球排行榜
          </h3>
          <div className="space-y-2">
            {['小明', '视界达人', '星空探险者', '护眼小卫士', '闪电反应'].map((name, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-xl p-3">
                <div className="flex items-center gap-2.5">
                  <span className={`text-xs font-bold w-5 ${
                    i === 0 ? 'text-amber-500' : i === 1 ? 'text-gray-400' : i === 2 ? 'text-amber-700' : 'text-gray-300 dark:text-gray-500'
                  }`}>
                    #{i + 1}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-300">{name}</span>
                </div>
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{Math.floor(Math.random() * 500 + 500)}分</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-3 text-center">邀请好友开启限时反应力竞赛!</p>
        </motion.div>
      </motion.div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 mx-auto sm:max-w-md md:max-w-lg sm:border-x z-40 bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border-t border-gray-100 dark:border-gray-700">
        <div className="max-w-2xl mx-auto flex items-center justify-center py-3 px-4">
          <button
            onClick={() => navigate('/')}
            className="px-8 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 shadow-md shadow-emerald-200 hover:shadow-lg hover:shadow-emerald-300 transition-all active:scale-95"
          >
            返回训练大厅
          </button>
        </div>
      </nav>
    </div>
  );
}
